/**
 * Is this string ONE emoji?
 *
 * Reactions used to be gated by a six-item allow-list in the engagement resolver, which
 * meant that Set was doing all of the input validation — the `emoji` column is bare
 * `TEXT` with no constraint, and GraphQL only promises `String!`. Opening the bar up to a
 * custom picker therefore cannot just delete it: something has to stand in its place, or
 * `toggleReaction` accepts a paragraph.
 *
 * The rule is deliberately "exactly one emoji", not "contains an emoji". A chip renders a
 * single glyph in a fixed-width pill, and a two-emoji string would become its own
 * distinct reaction forever — one nobody can reproduce from the picker.
 */

/**
 * A cheap ceiling checked BEFORE the pattern, so a megabyte of text is rejected by a
 * length read rather than by a matcher walking it.
 *
 * 64 rather than something tighter because a fully-specified RGI family — four people,
 * each with a skin-tone modifier, joined by three ZWJs — is 41 bytes on its own.
 */
export const MAX_REACTION_EMOJI_BYTES = 64;

/**
 * `\p{RGI_Emoji}` needs the `v` flag (ES2024). Built through `new RegExp` rather than
 * written as a literal for two reasons: `shared/tsconfig.json` targets ES2020, so a
 * literal `v` flag does not compile; and a runtime without `v` throws on construction,
 * which is catchable here and would not be catchable for a literal.
 *
 * RGI — "recommended for general interchange" — is the set vendors actually draw, which
 * is the same set the picker offers. It matches a whole ZWJ sequence as one unit, so a
 * full match is by itself the "exactly one grapheme" test.
 */
const RGI_EMOJI = (() => {
  try {
    return new RegExp('^\\p{RGI_Emoji}$', 'v');
  } catch {
    return null;
  }
})();

/**
 * Fallback for a runtime with no `v` flag (Safari < 17, Firefox < 116).
 *
 * An APPROXIMATION of RGI, not a replacement: one pictographic base with an optional
 * presentation selector or skin tone, optionally ZWJ-joined to more of the same. Flags
 * (a regional-indicator pair) and keycaps have their own shapes and are spelled out.
 * It admits a few sequences no vendor draws; it does not admit prose, which is the
 * property that matters here.
 */
const PICTOGRAPHIC_SEQUENCE =
  /^(?:\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*|[\u{1F1E6}-\u{1F1FF}]{2}|[0-9#*]️?⃣)$/u;

function byteLength(value: string): number {
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(value).length;
  // Every runtime we ship to has TextEncoder; this keeps the module total rather than
  // throwing on one that somehow does not.
  return value.length * 4;
}

const ZWJ = '\u200D';
const VS16 = '\uFE0F';

/** Whether a string is exactly one emoji, per whichever matcher this runtime has. */
function matches(value: string): boolean {
  return (RGI_EMOJI ?? PICTOGRAPHIC_SEQUENCE).test(value);
}

/**
 * Give an emoji its FULLY-QUALIFIED form.
 *
 * The picker's dataset stores several hundred emoji without the variation selector that
 * makes them render as pictures rather than text — its "heart" is a bare U+2764, where
 * the reaction palette's is U+2764 U+FE0F. Two things go wrong if that reaches the
 * server unchanged, and both are silent:
 *
 *   - a bare U+2764 is not a valid RGI emoji, so `isReactionEmoji` refuses it and the
 *     chip lights up and reverts — the same failure the old allow-list caused for 👍;
 *   - even if stored, it is a DIFFERENT string from the palette's heart, so the post
 *     would carry two heart chips that can never merge.
 *
 * Roughly one in six emoji the picker offers is affected, so this is not an edge case.
 * Both the bar and the resolver normalize, which is what keeps the optimistic chip and
 * the stored row the same string.
 */
export function normalizeReactionEmoji(value: string): string {
  if (typeof value !== 'string' || value.length === 0) return value;
  if (matches(value)) return value;

  // The common case: one base character missing its trailing selector.
  const appended = value + VS16;
  if (matches(appended)) return appended;

  // Keycaps carry the selector in the MIDDLE, and the dataset omits it: its "seven" is
  // "7" + U+20E3 where the qualified form is "7" + U+FE0F + U+20E3.
  const keycapMatch = /^([0-9#*])\u20E3$/.exec(value);
  if (keycapMatch) {
    const keycap = `${keycapMatch[1]}${VS16}\u20E3`;
    if (matches(keycap)) return keycap;
  }

  // ZWJ sequences need it INSIDE — "❤‍🔥" is qualified as "❤️‍🔥", not "❤‍🔥️".
  const rejoined = value
    .split(ZWJ)
    .map((part) => (matches(part) || !matches(part + VS16) ? part : part + VS16))
    .join(ZWJ);
  return matches(rejoined) ? rejoined : value;
}

/** Whether `value` is exactly one emoji, and therefore usable as a reaction. */
export function isReactionEmoji(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0) return false;
  if (byteLength(value) > MAX_REACTION_EMOJI_BYTES) return false;
  return matches(value);
}
